import { useEffect, useState } from 'react';
import { useOrg } from '@/features/team/OrgProvider';
import { ClientPicker } from './ClientPicker';
import { EstablishmentEditor } from './EstablishmentEditor';
import { ConfirmarBorrado } from '@/ui/ConfirmarBorrado';
import { MenuAcciones } from '@/ui/MenuAcciones';
import { EncabezadoPantalla, Seccion } from '@/ui/Sistema';
import { dondeEstaLaPantalla, llevarLaPantallaA } from '@/ui/scroll';
import { useBotonAtras } from '@/core/android/atras';
import { esErrorDeRed, mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { useBundleOpcional } from '@/core/offline/BundleProvider';
import { RepartoFactura } from '@/ui/RepartoFactura';
import { AvisoFlotante } from '@/ui/AvisoFlotante';
import {
  listParticipants,
  listSplitTemplates,
  type Participant,
  type SplitTemplate,
} from '@/features/works/works';
import { createClient, updateClient } from './clients';
import {
  createEstablishment,
  fetchOrganizationSettings,
  listAllClients,
  listEstablishmentsByClient,
  listEstablishments,
  setClientActive,
  type Client,
  type Establishment,
  type OrganizationSettings,
  deleteClient,
  deleteEstablishment,
  fetchClientInvoiceRecipients,
  setClientInvoiceRecipients,
  type DestinatarioFactura,
} from './clients';

/**
 * Clientes: la lista, y la ficha de uno.
 *
 * ## Son dos pantallas y antes eran una
 *
 * La ficha del cliente vivía ARRIBA de la lista, en la misma pantalla. Con
 * setenta clientes eso significaba que tocar el último abría su ficha fuera de
 * la vista: el gesto no mostraba nada, parecía que la aplicación no respondía,
 * y había que scrollear hasta arriba para descubrir que sí había hecho algo.
 *
 * Ahora abrir un cliente REEMPLAZA la lista, como el detalle de un trabajo
 * reemplaza al listado. Volver trae la lista con el scroll donde estaba: no
 * hay que bajar setenta nombres otra vez para seguir donde se iba.
 *
 * Por eso esta pantalla recibe su propio `onVolver` en lugar de que el marco le
 * ponga uno: cuando hay un cliente abierto, volver cierra la ficha; cuando no,
 * sale de Clientes. Un solo control de volver y siempre hace lo esperable,
 * incluso el botón Atrás del teléfono.
 */
export function ClientsPage({ onVolver }: { onVolver: () => void }) {
  const { activeOrg } = useOrg();
  const [clients, setClients] = useState<Client[]>([]);
  // Los campos de cada cliente, para el renglón secundario del listado.
  const [campos, setCampos] = useState<Record<string, Establishment[]>>({});
  const sinServidor = useSinConexion();
  const { bundle } = useBundleOpcional() ?? { bundle: null };
  const [selected, setSelected] = useState<Client | null>(null);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [settings, setSettings] = useState<OrganizationSettings | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  // `null` = nada esperando. Guarda qué se está por borrar, no si hay algo.
  const [borrandoCliente, setBorrandoCliente] = useState<Client | null>(null);
  const [borrandoCampo, setBorrandoCampo] = useState<Establishment | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [destinatarios, setDestinatarios] = useState<DestinatarioFactura[]>([]);
  const [nuevoCampo, setNuevoCampo] = useState('');
  // El alta de cliente, desplegada desde el botón. Tres estados y no uno: el
  // nombre sobrevive a un error de guardado, así que si la base rechaza el
  // nombre no hay que volver a escribirlo.
  const [creandoCliente, setCreandoCliente] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [guardandoCliente, setGuardandoCliente] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [plantillas, setPlantillas] = useState<SplitTemplate[]>([]);

  const orgId = activeOrg?.organization_id;

  /**
   * Dónde estaba la lista cuando se abrió un cliente.
   *
   * Sin esto, cerrar la ficha devuelve a la lista arriba de todo, y el cliente
   * que se acaba de mirar queda cuarenta nombres más abajo.
   */
  const [scrollDeLaLista, setScrollDeLaLista] = useState(0);

  const abrirCliente = (c: Client) => {
    setScrollDeLaLista(dondeEstaLaPantalla());
    setSelected(c);
    llevarLaPantallaA(0);
  };

  const cerrarCliente = () => {
    setSelected(null);
    // Recién en el próximo pintado existe la lista: antes no hay a dónde ir.
    requestAnimationFrame(() => llevarLaPantallaA(scrollDeLaLista));
  };

  // El botón Atrás del teléfono cierra la ficha antes de salir de Clientes: es
  // lo último que se abrió, y es lo que se espera que deshaga.
  useBotonAtras(() => {
    if (!selected) return false;
    cerrarCliente();
    return true;
  }, selected !== null);

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const ts = await listSplitTemplates(orgId);
        if (!cancelado) setPlantillas(ts);
      } catch {
        // Sin plantillas el selector queda con la opción de la organización.
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId]);

  const cambiarPlantilla = async (templateId: string | null) => {
    if (!selected) return;
    await updateClient(selected.id, { default_split_template_id: templateId });
    setSelected({ ...selected, default_split_template_id: templateId });
    setRecarga((n) => n + 1);
  };

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        // Sin servidor no se pregunta: se lee la copia operativa. Preguntar y
        // fallar dejaba la pantalla diciendo «0 en total», que no es cierto —no
        // hay cero clientes, no se pudo consultar— y además tapaba el hecho de
        // que los clientes SÍ estaban en el teléfono.
        if (sinServidor) {
          if (cancelado) return;
          setClients(bundle?.clientes ?? []);
          setCampos(bundle?.establecimientos ?? {});
          setParticipants(bundle?.participantes ?? []);
          setSettings(bundle?.ajustes ?? null);
          return;
        }

        const [lista, config, gente, porCliente] = await Promise.all([
          listAllClients(orgId),
          fetchOrganizationSettings(orgId),
          listParticipants(orgId),
          listEstablishmentsByClient(orgId),
        ]);
        if (cancelado) return;
        setClients(lista);
        setSettings(config);
        setParticipants(gente);
        setCampos(porCliente);
      } catch (e) {
        if (cancelado) return;
        // Un fallo de red no es un error de esta pantalla: es el modo sin
        // conexión, y se atiende mostrando lo que hay guardado.
        if (esErrorDeRed(e)) {
          setClients(bundle?.clientes ?? []);
          setCampos(bundle?.establecimientos ?? {});
          setParticipants(bundle?.participantes ?? []);
          setSettings(bundle?.ajustes ?? null);
        } else {
          setError(mensajeDeError(e));
        }
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, recarga]);

  // Los destinatarios del cliente elegido. Es a nombre de quién se le emite la
  // factura, y no tiene nada que ver con quién factura de la organización ni
  // con el reparto económico.
  // Al cambiar de cliente hay que limpiar lo que se estaba viendo, o por un
  // instante se muestran los destinatarios del anterior sobre el nuevo. Se
  // ajusta DURANTE el render y no en un efecto: en React 19 un `setState`
  // síncrono dentro de un efecto encadena renders.
  const [ultimoCliente, setUltimoCliente] = useState<string | null>(null);
  if ((selected?.id ?? null) !== ultimoCliente) {
    setUltimoCliente(selected?.id ?? null);
    setDestinatarios([]);
  }

  useEffect(() => {
    if (!selected) return;
    let cancelado = false;
    void (async () => {
      try {
        const r = await fetchClientInvoiceRecipients(selected.id);
        if (!cancelado) setDestinatarios(r);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [selected, recarga]);

  useEffect(() => {
    let cancelado = false;
    // El estado se toca siempre después de un await: nunca de forma síncrona
    // dentro del efecto (evita renders en cascada).
    void (async () => {
      try {
        const lista = await (selected ? listEstablishments(selected.id) : Promise.resolve([]));
        if (!cancelado) setEstablishments(lista);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [selected, recarga]);

  if (!activeOrg || !orgId) return null;

  const agregarCampo = async () => {
    if (!selected || nuevoCampo.trim() === '') return;
    setError(null);
    try {
      await createEstablishment({
        organizationId: orgId,
        clientId: selected.id,
        name: nuevoCampo,
      });
      setNuevoCampo('');
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const cancelarNuevoCliente = () => {
    setCreandoCliente(false);
    setNombreNuevo('');
    setError(null);
  };

  const crearCliente = async () => {
    const nombre = nombreNuevo.trim();
    if (nombre === '' || guardandoCliente) return;
    setError(null);
    setGuardandoCliente(true);
    try {
      const nuevo = await createClient({ organizationId: orgId, name: nombre });
      // Queda abierto: lo que uno quiere hacer después de crear un cliente es
      // cargarle los campos, y eso se hace en su ficha.
      abrirCliente(nuevo);
      setCreandoCliente(false);
      setNombreNuevo('');
      setAviso(`${nuevo.name} creado`);
      setRecarga((n) => n + 1);
    } catch (e) {
      // El formulario queda abierto y con el nombre puesto. Si la base rechaza
      // —un nombre repetido, por ejemplo— se corrige y se reintenta.
      setError(mensajeDeError(e));
    } finally {
      setGuardandoCliente(false);
    }
  };

  const guardarDestinatarios = async () => {
    if (!selected) return;
    setError(null);
    try {
      await setClientInvoiceRecipients(selected.id, destinatarios);
      setAviso('Destinatarios guardados');
    } catch (e) {
      // La base rechaza si no suman 100: el mensaje ya lo dice con el número.
      setError(mensajeDeError(e));
    }
  };

  const borrarCliente = async (c: Client) => {
    setError(null);
    try {
      await deleteClient(c.id);
      setBorrandoCliente(null);
      if (selected?.id === c.id) setSelected(null);
      setAviso(`${c.name} eliminado`);
      setRecarga((n) => n + 1);
    } catch (e) {
      // La base contesta cuántos trabajos hay y qué hacer. Se muestra tal cual:
      // «tiene 12 trabajos, borralos primero o desactivalo» es accionable;
      // «no se puede» manda a adivinar.
      setError(mensajeDeError(e));
      setBorrandoCliente(null);
    }
  };

  const borrarCampo = async (e0: Establishment) => {
    setError(null);
    try {
      await deleteEstablishment(e0.id);
      setBorrandoCampo(null);
      setAviso(`${e0.name} eliminado`);
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
      setBorrandoCampo(null);
    }
  };

  const alternarActivo = async (c: Client) => {
    setError(null);
    try {
      await setClientActive(c.id, !c.is_active);
      if (selected?.id === c.id) setSelected(null);
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  return (
    <div className="flex flex-col gap-5 p-4" data-testid="clients-page">
      <EncabezadoPantalla
        titulo={selected ? selected.name : 'Clientes'}
        nota={selected ? 'Reparto, facturación y campos' : 'Clientes y campos'}
        onVolver={selected ? cerrarCliente : onVolver}
      />

      {/* El buscador arriba y sin envoltorio: es lo primero que se usa.
          Debajo, el botón de alta.

          El alta ya existía dentro del buscador: escribiendo un nombre que no
          estuviera, al final de la lista aparecía «+ Crear …». Funcionaba y no
          la encontraba nadie, porque para llegar hay que buscar una cosa que
          uno sabe que no existe. Nadie hace eso: uno busca un botón.

          El renglón en gris que explicaba el truco se fue con él. Una ayuda que
          hay que leer para descubrir una función es la función mal puesta. */}
      {!selected && (
        <div className="flex flex-col gap-2">
          <ClientPicker
            organizationId={orgId}
            value={selected}
            onChange={(c) => (c ? abrirCliente(c) : setSelected(null))}
          />

          {!creandoCliente && (
            <button
              type="button"
              onClick={() => setCreandoCliente(true)}
              disabled={sinServidor}
              className="min-h-11 self-start rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-4 text-sm disabled:opacity-50"
              data-testid="abrir-nuevo-cliente"
            >
              + Nuevo cliente
            </button>
          )}

          {/* Sin señal no se ofrece y se dice por qué. Un cliente nuevo se guarda
            de verdad, con su identificador, y lo van a ver los demás: no es
            algo que pueda esperar en la cola como un trabajo. */}
          {sinServidor && !creandoCliente && (
            <p className="text-xs text-[var(--tinta-suave)]" data-testid="alta-sin-conexion">
              Sin conexión no se pueden crear clientes. Los trabajos sí: se guardan y se envían al
              volver la señal.
            </p>
          )}

          {creandoCliente && (
            <div
              className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-3"
              data-testid="form-nuevo-cliente"
            >
              <label className="flex flex-col gap-1 text-sm">
                Nombre del cliente
                <input
                  type="text"
                  value={nombreNuevo}
                  onChange={(ev) => setNombreNuevo(ev.target.value)}
                  // Enter guarda: es un formulario de un solo campo y obligar a
                  // bajar la mano hasta el botón no agrega nada.
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') void crearCliente();
                  }}
                  placeholder="Apellido o razón social"
                  autoFocus
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
                  data-testid="nuevo-cliente-nombre"
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void crearCliente()}
                  disabled={guardandoCliente || nombreNuevo.trim() === ''}
                  className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-sm text-[var(--sobre-accion)] disabled:opacity-50"
                  data-testid="guardar-nuevo-cliente"
                >
                  {guardandoCliente ? 'Guardando…' : 'Guardar'}
                </button>
                <button
                  type="button"
                  onClick={cancelarNuevoCliente}
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-4 text-sm"
                  data-testid="cancelar-nuevo-cliente"
                >
                  Cancelar
                </button>
              </div>
              <p className="text-xs text-[var(--tinta-tenue)]">
                Los campos se agregan después, abriendo el cliente.
              </p>
            </div>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="clients-error">
          {error}
        </p>
      )}

      {selected && (
        <section className="superficie p-4" data-testid="client-detail">
          {/* El reparto habitual del cliente pisa al de la organización y lo
              pisa, a su vez, lo que se elija en el trabajo. Está acá y no en
              Configuración porque es una característica DE ESTE cliente: «con
              los Rombola siempre vamos a medias» es algo que se sabe mirando la
              ficha del cliente, no una lista de plantillas. */}
          <label className="mb-3 flex flex-col gap-1 text-sm">
            Reparto habitual con este cliente
            <select
              value={selected.default_split_template_id ?? ''}
              onChange={(e) => void cambiarPlantilla(e.target.value || null)}
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2"
              data-testid="client-split-template"
            >
              <option value="">Usar el de la organización</option>
              {plantillas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <span className="text-xs text-[var(--tinta-tenue)]">
              Se aplica solo al crear un trabajo, y se puede cambiar en ese trabajo.
            </span>
          </label>

          {/* A nombre de quién se emite la factura de este cliente.
              Existe porque hay clientes que son dos personas —un campo de un
              matrimonio, una sucesión— y sin esto se le termina facturando el
              100 % a uno de los dos sin querer. No se resuelve duplicando el
              cliente: sería el mismo campo dos veces en todas las listas. */}
          <div className="mb-4 flex flex-col gap-2 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-3">
            <p className="text-sm font-medium">Destinatarios de facturación</p>
            <RepartoFactura lineas={destinatarios} onChange={setDestinatarios} />
            <button
              type="button"
              onClick={() => void guardarDestinatarios()}
              className="min-h-11 self-start rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-4 text-sm"
              data-testid="guardar-destinatarios"
            >
              Guardar destinatarios
            </button>
          </div>

          <p className="mb-3 text-sm text-[var(--tinta-suave)]">Establecimientos</p>

          <div className="flex flex-col gap-3">
            {establishments.length === 0 && (
              <p className="text-sm text-[var(--tinta-suave)]">
                Todavía no tiene establecimientos.
              </p>
            )}
            {establishments.map((e) => (
              <div key={e.id} className="flex flex-col gap-2">
                <EstablishmentEditor
                  establishment={e}
                  settings={settings}
                  participants={participants}
                  onSaved={() => setRecarga((n) => n + 1)}
                />
                <button
                  type="button"
                  onClick={() => setBorrandoCampo(e)}
                  className="min-h-11 self-start text-sm text-[var(--peligro)] underline "
                  data-testid="delete-establishment"
                >
                  Borrar campo
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4 flex gap-2">
            <input
              type="text"
              value={nuevoCampo}
              onChange={(ev) => setNuevoCampo(ev.target.value)}
              placeholder="Nombre del campo"
              className="min-h-11 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
              data-testid="new-establishment-name"
            />
            <button
              type="button"
              onClick={() => void agregarCampo()}
              className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-sm text-[var(--sobre-accion)]"
              data-testid="add-establishment"
            >
              Agregar
            </button>
          </div>
        </section>
      )}

      {!selected && (
        <Seccion
          titulo="Todos los clientes"
          nota={
            // «0 en total» sin conexión era mentir con un número: no hay cero
            // clientes, no se pudo consultar. Se dice qué se está mirando.
            sinServidor && !bundle
              ? 'Sin conexión y todavía no hay clientes descargados en este dispositivo. Conectate una vez para prepararlo para uso offline.'
              : sinServidor
                ? `${clients.length} guardados en este teléfono · sin conexión`
                : `${clients.length} en total`
          }
        >
          <ul className="flex flex-col divide-y divide-[var(--linea)]" data-testid="clients-list">
            {clients.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 py-1">
                <button
                  type="button"
                  onClick={() => abrirCliente(c)}
                  className="min-h-11 min-w-0 flex-1 py-1 text-left"
                  data-testid="client-row"
                >
                  <span
                    className={`block truncate font-medium ${
                      c.is_active ? '' : 'text-[var(--tinta-tenue)]'
                    }`}
                  >
                    {c.name}
                    {!c.is_active && <span className="ml-2 text-xs">(inactivo)</span>}
                    {c.record_kind === 'legacy_placeholder' && (
                      <span className="ml-2 rounded-full bg-[var(--aviso-fondo)] px-2 py-0.5 text-xs text-[var(--aviso)]">
                        legacy
                      </span>
                    )}
                  </span>
                  {/* Los campos, como texto secundario. Son lo que distingue a
                    dos clientes con nombre parecido, y estaban sólo adentro
                    del detalle. */}
                  {(campos[c.id]?.length ?? 0) > 0 && (
                    <span className="block truncate text-sm text-[var(--tinta-suave)]">
                      {campos[c.id]!.map((e) => e.name).join(' · ')}
                    </span>
                  )}
                </button>

                {/* Un menú, no dos botones por renglón. Con setenta clientes
                  eran ciento cuarenta botones repetidos, y encontrar el
                  cliente costaba más que hacerle algo. */}
                <MenuAcciones
                  etiqueta={`Acciones de ${c.name}`}
                  testId="client-menu"
                  acciones={[
                    {
                      etiqueta: 'Ver y editar',
                      testId: 'ver-cliente',
                      onClick: () => abrirCliente(c),
                    },
                    {
                      etiqueta: c.is_active ? 'Desactivar' : 'Reactivar',
                      testId: 'toggle-active',
                      onClick: () => void alternarActivo(c),
                    },
                    {
                      etiqueta: 'Eliminar',
                      testId: 'delete-client',
                      destructiva: true,
                      onClick: () => setBorrandoCliente(c),
                    },
                  ]}
                />
              </li>
            ))}
          </ul>

          <p className="text-xs text-[var(--tinta-tenue)]">
            Desactivar y borrar son cosas distintas: desactivar saca al cliente del buscador y le
            deja los trabajos; borrar lo hace desaparecer, y sólo se puede si no tiene ninguno.
          </p>
        </Seccion>
      )}
      {/* Fuera del flujo a propósito: son `fixed`. Da igual dónde estén en el
          árbol, y el scroll de la lista no se mueve ni al abrirlas ni al
          cerrarlas. */}
      {borrandoCliente && (
        <ConfirmarBorrado
          que={`el cliente «${borrandoCliente.name}»`}
          detalle="Si tiene trabajos cargados, la base te va a decir cuántos y no se borra nada."
          ocupado={false}
          onCancelar={() => setBorrandoCliente(null)}
          onConfirmar={() => void borrarCliente(borrandoCliente)}
          testId="confirmar-borrar-cliente"
        />
      )}
      {borrandoCampo && (
        <ConfirmarBorrado
          que={`el campo «${borrandoCampo.name}»`}
          detalle="Si tiene trabajos cargados, la base te va a decir cuántos y no se borra nada."
          onCancelar={() => setBorrandoCampo(null)}
          onConfirmar={() => void borrarCampo(borrandoCampo)}
          testId="confirmar-borrar-campo"
        />
      )}
      <AvisoFlotante texto={aviso} onCerrar={() => setAviso(null)} />
    </div>
  );
}
